import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreateTemplate.module.css';
import CreationBar from '../../../Components/Utils/Actions/CreationBar/CreationBar';
import { CreateClientQuestionnaireTemplateByTitle, GetMandatedMesmaTemplates, CreateTemplateFromMesmaTemplate, CreateImproveTemplateByTitle, CreateClientTemplateByTitle, CreateClientDevelopTemplateByTitle, GetMandatedMesmaTemplatesDisplayInfos } from '../../../API/TemplateBank/TemplateBank';
import { useAppSelector } from '../../../Redux/Store/Store';
import { getClient, getDevelopEnabled } from '../../../Redux/Store/Slices/client';
import { toast } from 'react-toastify';
import Button from '../../../Components/Utils/Button/Button';
import DropDownList from '../../../Components/Utils/DropDownList/DropDownList';
import { IDropDownListItem } from '../../../Types/DropDownList/IDropDownListItem';
import LoadingPopup from '../../../Components/Utils/LoadingPopup/LoadingPopup';
import { AreaDisplayInfo } from '../../../Types/AreaDisplayInfo/AreaDisplayInfo';
import { ProductType } from '../../../Types/ProductType';

const CreateTemplate = () => {
  const navigate = useNavigate();
  const client = useAppSelector(getClient);
  const developEnabled = useAppSelector(getDevelopEnabled);
  const [selectedTemplate, setSelectedTemplate] = useState<IDropDownListItem | null>(null);
  const [templateType, setTemplateType] = useState<IDropDownListItem | null>(null);
  const [templateInfos, setTemplateInfos] = useState<AreaDisplayInfo[]>([]);
  const [mesmaTemplates, setMesmaTemplates] = useState<IDropDownListItem[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [typeOptions, setTypeOptions] = useState<IDropDownListItem[]>([
    { value: 'Evaluate', text: 'Evaluate', colour: '#24394F', textColour: '#FFFFFF' },
    { value: 'Improve', text: 'Track', colour: '#52BFA2', textColour: '#FFFFFF' },
  ]);

  useEffect(() => {
    const evaluateOption: IDropDownListItem = { value: 'Evaluate', text: 'Evaluate', colour: '#24394F', textColour: '#FFFFFF' };
    const trackOption: IDropDownListItem = { value: 'Improve', text: 'Track', colour: '#52BFA2', textColour: '#FFFFFF' };
    const developOption: IDropDownListItem = { value: 'Develop', text: 'Develop', colour: '#4F46E5', textColour: '#FFFFFF' };
    const showDevelop = developEnabled === true;
    setTypeOptions(showDevelop ? [evaluateOption, trackOption, developOption] : [evaluateOption, trackOption]);
    setTemplateType((prev) => (prev?.value === 'Develop' && !showDevelop ? null : prev));
  }, [developEnabled]);

  // Fetch mandated Mesma templates on component mount
  useEffect(() => {
    const fetchMesmaTemplates = async () => {
      if (client) {
        setIsLoadingTemplates(true);
        try {
          const templates = await GetMandatedMesmaTemplatesDisplayInfos(client.id);
          setTemplateInfos(templates);
          setMesmaTemplates(templates.map(template => ({
            text: template.title,
            value: template.id,
            colour: undefined,
            textColour: undefined
          })));
        } catch (error) {
          console.error('Failed to fetch Mesma templates:', error);
          toast.error('Failed to load Mesma templates');
        } finally {
          setIsLoadingTemplates(false);
        }
      }
    };
    
    fetchMesmaTemplates();
  }, [client]);

  const createTemplate = async (title: string) => {
    // Prevent multiple clicks - if already creating, return early
    if (isCreating) {
      return;
    }
    
    if (!templateType) {
      toast.warn('Please select a template type first');
      return;
    }
    
    if (!client) {
      return;
    }
    
    setIsCreating(true);
    try {
      let templateId: string;
      const encodedTitle = encodeURIComponent(title);
      
      if(templateType?.value === "Improve"){
        templateId = await CreateClientTemplateByTitle(title, client.id);
        navigate(`/admin/template-editor/improve/${templateId}/${encodedTitle}/improve-template-creation`);
      }
      else if(templateType?.value === "Develop"){
        templateId = await CreateClientQuestionnaireTemplateByTitle(title, client.id, ProductType.Develop);
        navigate(`/admin/create-a-template/develop/${templateId}/${encodedTitle}/define-fields`);
      }
      else if(templateType?.value === "Evaluate"){
        templateId = await CreateClientQuestionnaireTemplateByTitle(title, client.id, ProductType.Evaluate);
        navigate(`/admin/create-a-template/evaluate/${templateId}/${encodedTitle}/define-fields`);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to create template. Please try again.';
      toast.error(errorMessage);
      setIsCreating(false); // Reset on error so user can try again
    }
    // Note: Don't reset isCreating on success since we navigate away
  };

  const handleTemplateSelect = (template: IDropDownListItem) => {
    setSelectedTemplate(template);
  };

  const handleImportTemplate = async () => {
    if (selectedTemplate && client) {
      setIsImporting(true);
      try {
        const templateId = await CreateTemplateFromMesmaTemplate(
          selectedTemplate.value, 
          selectedTemplate.text, 
          client.id
        );
        toast.success(`Template "${selectedTemplate.text}" imported successfully!`);

        const info = templateInfos.find(info => info.id === selectedTemplate.value);
        if(info){
          // Debug: Log the productType to help identify issues
          console.log('=== IMPORT TEMPLATE DEBUG ===');
          console.log('Template ID:', selectedTemplate.value);
          console.log('Template Title:', selectedTemplate.text);
          console.log('Info productType:', info.productType);
          console.log('Info productType type:', typeof info.productType);
          console.log('ProductType.Develop:', ProductType.Develop);
          console.log('ProductType.Improve:', ProductType.Improve);
          console.log('ProductType.Evaluate:', ProductType.Evaluate);
          console.log('Is Develop?', info.productType === ProductType.Develop);
          console.log('Is Improve?', info.productType === ProductType.Improve);
          console.log('Is Evaluate?', info.productType === ProductType.Evaluate);
          console.log('Full info object:', JSON.stringify(info, null, 2));
          console.log('===========================');
          
          // Convert productType to number if it's a string (defensive check)
          const productType = typeof info.productType === 'string' 
            ? parseInt(info.productType, 10) 
            : info.productType;
          
          // Check Develop first to ensure it's not misidentified as Improve
          if(productType === ProductType.Develop || productType === 3){
            console.log('Navigating to DEVELOP workflow');
            navigate(`/admin/create-a-template/develop/${templateId}/${encodeURIComponent(info.title)}/define-fields`);
          }
          else if(productType === ProductType.Improve || productType === 2){
            console.log('Navigating to IMPROVE workflow');
            navigate(`/admin/template-editor/improve/${templateId}/${encodeURIComponent(info.title)}/improve-template-creation`);
          }
          else{
            console.log('Navigating to EVALUATE workflow (default)');
            // Default to Evaluate for Evaluate, Enquire, or undefined productType
            navigate(`/admin/create-a-template/evaluate/${templateId}/${encodeURIComponent(info.title)}/define-fields`);
          }
        } else {
          // Fallback if info is not found - this shouldn't happen but handle gracefully
          console.error('Template info not found for imported template:', selectedTemplate.value);
          console.error('Available template IDs:', templateInfos.map(t => t.id));
          toast.error('Template imported but navigation failed. Please navigate manually.');
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || 'Failed to import template. Please try again.';
        toast.error(errorMessage);
      } finally {
        setIsImporting(false);
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.splitLayout}>
        
        {/* Left Section - Create Own Template */}
        <div className={styles.leftSection}>
          <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconWrapper}>
                <img src="/img/NavIcons/product.svg" alt="Create" className={styles.sectionIcon} />
              </div>
              <h2>Create Your Own Template</h2>
              <p>Create and edit diagnostic templates.</p>
            </div>
            <div className={styles.actionArea}>
              <div className={styles.creationBarWrapper} style={{ marginBottom: '0.75rem' }}>
                <DropDownList
                  value={templateType?.value || ''}
                  placeholder="Select template type"
                  data={typeOptions}
                  onChange={setTemplateType}
                  style={{ width: '100%' }}
                />
              </div>
              <div className={styles.creationBarWrapper}>
                {templateType ? (
                  <CreationBar
                    onCreateHandler={createTemplate}
                    placeholder={'Enter title for new template'}
                    noTextMessage={'The Title field is required'}
                    disabled={isCreating}
                  />
                ) : (
                  <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.75rem' }}>
                    Select a template type to start creating.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Import Mesma Template */}
        <div className={styles.rightSection}>
          <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
              <div className={styles.iconWrapper}>
                <img src="/img/NavIcons/library.svg" alt="Import" className={styles.sectionIcon} />
              </div>
              <h2>Import Mesma Template</h2>
              <p>Choose from professionally designed diagnostic templates created by our specialist team.</p>
            </div>
            
            <div className={styles.actionArea}>
              <div className={styles.importControls}>
                {isLoadingTemplates ? (
                  <p style={{ textAlign: 'center', color: '#6c757d' }}>Loading templates...</p>
                ) : mesmaTemplates.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6c757d' }}>No mandated templates available</p>
                ) : (
                  <>
                    <DropDownList
                      value={selectedTemplate?.value || ''}
                      placeholder="Select a template"
                      data={mesmaTemplates}
                      onChange={handleTemplateSelect}
                      style={{ marginBottom: '20px', width: '100%' }}
                    />
                    <Button 
                      clickHandler={handleImportTemplate}
                      colour="#24394F"
                      textColour="#FFFFFF"
                      disabled={!selectedTemplate || isImporting}
                      style={{ width: '100%' }}
                    >
                      {isImporting ? 'Importing...' : 'Import Template'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
      
      <LoadingPopup 
        isVisible={isImporting || isCreating} 
        message={isImporting ? "Importing..." : "Creating..."} 
      />
    </div>
  );
};

export default CreateTemplate;
